/**
 * StageRouter — The stage's single mounted component. Holds the current view
 * and swaps it in response to `navigate:*` bus events.
 *
 * Route kinds:
 *   - home              → AuthorListView
 *   - author            → AuthorDetailView (placeholder until task 9)
 *   - influence-graph   → InfluenceGraphView (placeholder until task 12)
 *   - theme-map         → ThemeMapView (placeholder until task 13)
 *
 * Discriminated union pattern: adding a new route = add to Route + add a case
 * to mountRoute() + emit `navigate:<kind>` from anywhere on the bus.
 */

import { BaseComponent } from '@/core/BaseComponent';
import type { ComponentContext } from '@/core/BaseComponent';
import { AuthorListView } from './AuthorListView';
import { AuthorDetailView } from './AuthorDetailView';
import { InfluenceGraphView } from './InfluenceGraphView';
import type { InfluenceState } from './InfluenceGraphView';
import { ThemeMapView } from './ThemeMapView';
import type { ThemeMapState } from './ThemeMapView';

export type Route =
  | { kind: 'home' }
  /** themeId opzionale: atterrando sulla scheda si scrolla al trattamento
   *  di quel tema (deep-link dalla Mappa dei temi). */
  | { kind: 'author'; id: string; themeId?: string }
  | { kind: 'influence-graph' }
  | { kind: 'theme-map' };

export class StageRouter extends BaseComponent {
  private currentRoute: Route = { kind: 'home' };
  private currentView: BaseComponent | null = null;
  private slot!: HTMLElement;
  /** Storico delle rotte visitate → il "← Indietro" dell'autore torna alla
   *  vista d'origine (Mappa dei temi / Rete delle influenze / Autori), non
   *  sempre alla lista autori. */
  private history: Array<{ route: Route; state?: unknown }> = [];

  protected async onInit(): Promise<void> {
    this.element.classList.add('stage-router');
    this.slot = document.createElement('div');
    this.slot.className = 'stage-view-slot';
    this.element.appendChild(this.slot);

    // Wire navigation events from the bus
    this.addCleanup(this.context.bus.on<{ id: string; themeId?: string }>('navigate:author', ({ id, themeId }) => {
      this.navigate({ kind: 'author', id, themeId });
    }));
    this.addCleanup(this.context.bus.on('navigate:home', () => {
      this.navigate({ kind: 'home' });
    }));
    this.addCleanup(this.context.bus.on('navigate:influence-graph', () => {
      this.navigate({ kind: 'influence-graph' });
    }));
    this.addCleanup(this.context.bus.on('navigate:theme-map', () => {
      this.navigate({ kind: 'theme-map' });
    }));
    // "Indietro" dall'autore → torna alla rotta E allo stato da cui si è partiti.
    this.addCleanup(this.context.bus.on('navigate:back', () => {
      const prev = this.history.pop();
      this.navigate(prev?.route ?? { kind: 'home' }, { isBack: true, restoreState: prev?.state });
    }));

    await this.mountRoute(this.currentRoute);
  }

  async navigate(route: Route, opts: { isBack?: boolean; restoreState?: unknown } = {}): Promise<void> {
    if (sameRoute(route, this.currentRoute)) return;
    if (!opts.isBack) {
      // cattura lo stato della vista che stiamo lasciando (se lo espone)
      const capture = (this.currentView as { captureState?: () => unknown } | null)?.captureState?.();
      this.history.push({ route: this.currentRoute, state: capture });
    }
    this.currentRoute = route;
    if (this.currentView) {
      this.currentView.dispose();
      this.currentView = null;
    }
    this.slot.innerHTML = '';
    // RESET COMPLETO dello slot: le viste aggiungono classi/attributi allo
    // stesso elemento (classList.add('influence-graph-view') ecc.) che si
    // ACCUMULAVANO tra una navigazione e l'altra — chi guarda le classi
    // (es. cattura mappe → kind) trovava più viste insieme e sbagliava.
    this.slot.className = 'stage-view-slot';
    for (const attr of Array.from(this.slot.attributes)) {
      if (attr.name.startsWith('data-') || attr.name === 'style') this.slot.removeAttribute(attr.name);
    }
    await this.mountRoute(route, opts.restoreState);
    this.context.bus.emit('stage:route-changed', route);
  }

  private async mountRoute(route: Route, restoreState?: unknown): Promise<void> {
    const ctx: ComponentContext = this.context;
    const slot = this.slot;
    let view: BaseComponent;

    switch (route.kind) {
      case 'home':
        view = new AuthorListView(slot, ctx);
        break;
      case 'author': {
        const origin = this.history[this.history.length - 1]?.route ?? { kind: 'home' };
        view = new AuthorDetailView(slot, ctx, {
          authorId: route.id,
          scrollToThemeId: route.themeId,
          backLabel: this.backLabel(origin),
        });
        break;
      }
      case 'influence-graph':
        view = new InfluenceGraphView(slot, ctx, restoreState as InfluenceState | undefined);
        break;
      case 'theme-map':
        view = new ThemeMapView(slot, ctx, restoreState as ThemeMapState | undefined);
        break;
    }

    await view.init();
    view.show();
    this.currentView = view;
  }

  /** Etichetta del "← Indietro" in base alla rotta d'origine. */
  private backLabel(route: Route): string {
    const t = (k: string) => this.context.i18n.t(k);
    switch (route.kind) {
      case 'theme-map': return t('nav.themeMap');
      case 'influence-graph': return t('nav.influenceGraph');
      case 'author': return t('sidebar.back');
      default: return t('nav.authors');
    }
  }

  protected onDispose(): void {
    if (this.currentView) {
      this.currentView.dispose();
      this.currentView = null;
    }
  }
}

function sameRoute(a: Route, b: Route): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'author' && b.kind === 'author') return a.id === b.id;
  return true;
}
